import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ChevronLeft, Copy, ExternalLink, FileDown, FileText } from 'lucide-react'

import type { Deal, FinancialYear, DealStatus } from '@/types/deal'
import { ENGAGEMENT_TYPE_LABELS } from '@/types/deal'
import {
  apiClient,
  getDeal,
  getDealNormalization,
  getDealFindings,
  getDealJourney,
  getDealReport,
  patchFindingScore,
  type RawFinding,
  type DealNormalization,
  type DealFindingsResponse,
  type DealJourney,
} from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { rememberLastDeal } from '@/lib/lastDeal'
import { FindingsTable, type Finding as FindingsTableFinding } from '@/components/FindingsTable'
import { JourneyRail } from '@/components/JourneyRail'
import { ScopeBadge } from '@/components/ScopeBadge'
import { ScopeSelector } from '@/components/ScopeSelector'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DocumentList } from '@/components/DocumentList'
import { NWCPegCard } from '@/components/qoe/NWCPegCard'
import { BalanceSheetInputs } from '@/components/qoe/BalanceSheetInputs'
import { BankCorroborationCard } from '@/components/qoe/BankCorroborationCard'
import { ProofOfCashInputs } from '@/components/qoe/ProofOfCashInputs'
import { IndustryBenchmarkCard } from '@/components/qoe/IndustryBenchmarkCard'
import { ExecutiveSummaryPanel } from '@/components/qoe/ExecutiveSummaryPanel'
import { BuyerDealBriefPanel } from '@/components/qoe/BuyerDealBriefPanel'
import { EquityBridgeCard } from '@/components/qoe/EquityBridgeCard'
import { EquityBridgeInputs } from '@/components/qoe/EquityBridgeInputs'
import { DocumentRequestConfig } from '@/components/qoe/DocumentRequestConfig'
import { SourceDocumentUploader } from '@/components/qoe/SourceDocumentUploader'
import { NegotiatedScopePanel } from '@/components/qoe/NegotiatedScopePanel'
import { CATALOGUE_SIZE } from '@/lib/qoeCatalogue.generated'

// ── Types ──────────────────────────────────────────────────────────────────────

// QOE-CPA-FIXPACK-1 D2: the normalization response is FLAT — financial_years,
// status and approved_at all sit at the top level. This page used to read
// `normRes.data`, a key that does not exist on the wire, so normData was
// permanently null and every consumer concluded "financials not yet entered"
// no matter how many years had been entered and CPA-approved.
// The shape now lives with the call, in api/client.ts.

interface ReportData {
  url: string | null
  generated_at: string | null
  filename: string | null
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<DealStatus, string> = {
  setup: 'Setup',
  seller_intake: 'Seller Intake',
  in_analysis: 'In Analysis',
  report_ready: 'Report Ready',
  delivered: 'Delivered',
  closed: 'Closed',
  withdrawn: 'Withdrawn',
}

const STATUS_BADGE_VARIANT: Record<DealStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  setup: 'outline',
  seller_intake: 'secondary',
  in_analysis: 'default',
  report_ready: 'default',
  delivered: 'secondary',
  closed: 'outline',
  withdrawn: 'destructive',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtUsd(n: number | null): string {
  if (n === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// ── DealDetail ────────────────────────────────────────────────────────────────

export function DealDetail() {
  const { dealId } = useParams<{ dealId: string }>()
  const navigate = useNavigate()

  const [deal, setDeal] = useState<Deal | null>(null)
  const [normData, setNormData] = useState<DealNormalization | null>(null)
  const [rawFindings, setRawFindings] = useState<RawFinding[]>([])
  // QOE-CPA-FIXPACK-1 D1/D4: the scope envelope that comes back with the
  // findings — in_scope_count is 17/54/74 for THIS deal, already adjusted for
  // any per-deal negotiated delta. Never CATALOGUE_SIZE.
  const [findingsScope, setFindingsScope] = useState<DealFindingsResponse | null>(null)
  const [report, setReport] = useState<ReportData>({ url: null, generated_at: null, filename: null })
  const [docs, setDocs] = useState<any[]>([])
  // QOE-TRUTH-AND-JOURNEY-1 R9-R13: the Journey Rail's own data. Fetched
  // separately (like docs below) so a hiccup on this one endpoint degrades
  // to "no rail shown" rather than failing the whole page.
  const [journey, setJourney] = useState<DealJourney | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [showScopeWizard, setShowScopeWizard] = useState(false)
  // FIELDS-1 P0: bumped when the proof-of-cash form saves, so the corroboration
  // card below it re-reads instead of showing a stale "not yet provided".
  const [bankRefreshKey, setBankRefreshKey] = useState(0)
  // FIELDS-1 P1a: same pattern for the balance sheet — the peg is computed from
  // it, so a save has to invalidate the card sitting underneath the form.
  const [nwcRefreshKey, setNwcRefreshKey] = useState(0)
  // FIELDS-1 P1b: and again for the equity bridge intake.
  const [equityRefreshKey, setEquityRefreshKey] = useState(0)

  useEffect(() => {
    if (!dealId) return
    const id: string = dealId
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [dealRes, normRes, findingsRes] = await Promise.all([
          getDeal(id),
          getDealNormalization(id),
          getDealFindings(id),
        ])
        if (cancelled) return
        setDeal(dealRes)
        rememberLastDeal({ deal_id: id, business_name: dealRes.business_name })
        setNormData(normRes ?? null)
        setRawFindings(findingsRes.findings)
        setFindingsScope(findingsRes)
        const reportRes = await getDealReport(id)
        if (cancelled) return
        setReport(reportRes)
      } catch (err: unknown) {
        if (cancelled) return
        const status = (err as { response?: { status: number } })?.response?.status
        if (status === 404) {
          toast.error('Deal not found')
          navigate('/dashboard')
          return
        }
        setError(formatApiError(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [dealId, retryCount, navigate])

  // Fetch documents for this deal
  useEffect(() => {
    if (!dealId) return
    const id: string = dealId
    let cancelled = false

    async function loadDocs() {
      try {
        const res = await apiClient.get<unknown[]>(
          `/v1/documents?deal_id=${encodeURIComponent(id)}`
        )
        if (!cancelled) setDocs(Array.isArray(res.data) ? res.data : [])
      } catch {
        // Gracefully fail — documents are optional
      }
    }

    loadDocs()
    return () => { cancelled = true }
  }, [dealId])

  // Fetch the Journey Rail payload for this deal (QOE-TRUTH-AND-JOURNEY-1 R9-R13)
  useEffect(() => {
    if (!dealId) return
    const id: string = dealId
    let cancelled = false

    async function loadJourney() {
      try {
        const res = await getDealJourney(id)
        if (!cancelled) setJourney(res)
      } catch {
        // Gracefully fail — the rail is supplementary, never load-bearing
        // for the rest of the page.
      }
    }

    loadJourney()
    return () => { cancelled = true }
  }, [dealId])

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`))
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-6 space-y-4 max-w-7xl mx-auto">
        <Skeleton className="h-5 w-36" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRetryCount(c => c + 1)}
              className="ml-4"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!deal) return null

  // ── Map findings ──────────────────────────────────────────────────────────

  const mappedFindings: FindingsTableFinding[] = rawFindings.map(f => ({
    number: f.number,
    title: f.title,
    body: f.body,
    section: f.section,
    score: f.score.toLowerCase() as FindingsTableFinding['score'],
    score_note: f.score_note ?? undefined,
    is_essentials: f.is_essentials,
    evidence: f.evidence,
    // FINDINGS-DOMAIN-1: carry the lifecycle state through so the grid can
    // distinguish "out of scope" from "still pending", exactly as the report does.
    state: f.state,
    in_scope: f.in_scope,
    required_inputs: f.required_inputs ?? undefined,
  }))

  // FINDINGS-COUNT-1: the tab used to read rawFindings.length — the size of the
  // 74-check catalogue, which is identical for every deal. A deal with no
  // financials entered announced "Findings (74)" as if 74 checks had come back.
  // Count only what actually ran.
  const firedCount = rawFindings.filter(f => f.fired).length

  // QOE-CPA-FIXPACK-1 D1/D4: ONE scope number, used by every string on this
  // page. A Standard deal is 54 checks, not 74 — the API already resolves that
  // (tier + per-deal negotiated delta) and returns it as in_scope_count. Fall
  // back to counting the rows, and only then to the catalogue size, so an older
  // backend degrades to "everything is in scope" instead of showing nothing.
  const inScopeCount =
    findingsScope?.in_scope_count ??
    (rawFindings.length > 0
      ? rawFindings.filter(f => f.in_scope !== false).length
      : CATALOGUE_SIZE)
  // FindingsTable's tier prop only distinguishes the Essentials subset; every
  // deeper scope is driven by each row's own in_scope flag. Passing a hardcoded
  // "comprehensive" made its empty-section copy claim the Comprehensive tier on
  // a lite deal.
  const scopeDepth = (findingsScope?.scope_tier ?? deal.scope_tier ?? '').toLowerCase()
  const findingsTier: 'essentials' | 'comprehensive' =
    scopeDepth === 'lite' ? 'essentials' : 'comprehensive'

  const financialYears: FinancialYear[] = normData?.financial_years ?? []
  const hasFinancials = financialYears.length > 0
  // D2: approved-normalization state, read from the same call that carries the
  // financials. 'draft' until a CPA approves it.
  const normalizationApproved = (normData?.status ?? '').toLowerCase() === 'approved'

  const scopeConfigured = deal.scope_score != null

  // QOE-TRUTH-AND-JOURNEY-1 R14: whichever condition disables the portal-link
  // buttons, name it — never let the explanation disappear while the buttons
  // stay dead. Scope-not-configured takes priority (it's the common case and
  // the one with a CTA); a missing token with scope already configured is
  // the rarer "something's off, not on you" case.
  const portalLinkReason = !scopeConfigured
    ? "Portal links become shareable once the engagement scope is set — until then the seller's uploads would be refused."
    : (!deal.seller_token || !deal.buyer_token)
      ? 'Portal link is not available for this deal yet — refresh, or contact support if this persists.'
      : null

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Dashboard
      </Link>

      {/* Scope-not-configured banner */}
      {!scopeConfigured && (
        <div className="flex items-center justify-between rounded-lg border border-amber-300
                        bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span>
            ⚠ Engagement scope has not been configured. Document uploads are blocked until scope is set.
          </span>
          <button
            type="button"
            onClick={() => setShowScopeWizard(true)}
            className="ml-4 shrink-0 rounded bg-amber-600 px-3 py-1.5 text-xs font-medium
                       text-white hover:bg-amber-700"
          >
            Configure Scope
          </button>
        </div>
      )}

      {/* Scope wizard modal */}
      {showScopeWizard && dealId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-pal-overlay/40">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Set Engagement Scope</h2>
              <button
                type="button"
                onClick={() => setShowScopeWizard(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>
            <ScopeSelector
              dealId={dealId}
              // QOE-CPA-FIXPACK-1 D5: industry is captured once, on the New
              // Engagement form. The wizard prefills its complexity weight from
              // it instead of asking again.
              vertical={deal.vertical}
              onComplete={(tier, score) => {
                setDeal((prev) => prev ? { ...prev, scope_tier: tier, scope_score: score } : prev)
                setShowScopeWizard(false)
                // WP-4 (D7, QOE-DEV-BATCH-1): this is the complexity score
                // (range 5-15), not MIR points — the two were both rendering
                // as "pts" and reading as the same quantity. Word it the same
                // way the scope review step already does.
                toast.success(`Scope set: ${tier} (complexity score ${score})`)
              }}
              onCancel={() => setShowScopeWizard(false)}
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {deal.business_name}
            </h1>
            <Badge variant={STATUS_BADGE_VARIANT[deal.status]}>
              {STATUS_LABELS[deal.status]}
            </Badge>
            <ScopeBadge
              tier={deal.scope_tier ?? null}
              override={deal.scope_override}
              unconfigured={!scopeConfigured}
            />
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {deal.loi_price ? `LOI ${fmtUsd(deal.loi_price)} · ` : ''}
            {ENGAGEMENT_TYPE_LABELS[deal.engagement_type] ?? deal.engagement_type} · {deal.analyst ?? 'unassigned'}
          </p>
        </div>
      </div>

      {/* Journey Rail (QOE-TRUTH-AND-JOURNEY-1 R13): QoE only — Attest,
          Valuation and OTCID keep their own step components (R13/brief §3d).
          Absent until the journey fetch resolves; it never blocks the rest
          of the page (see the loadJourney effect above). */}
      {journey && (
        <JourneyRail
          stages={journey.stages}
          current={journey.current}
          completed={journey.completed}
          gates={journey.gates}
          nextAction={journey.next_action}
          stageLabels={STATUS_LABELS}
        />
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="max-w-full flex-nowrap justify-start overflow-x-auto overflow-y-hidden">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="doc-requests">
            <span className="md:hidden">Docs</span>
            <span className="hidden md:inline">Document Requests</span>
          </TabsTrigger>
          <TabsTrigger value="findings">
            <span className="md:hidden">Findings</span>
            <span className="hidden md:inline">Findings ({firedCount})</span>
          </TabsTrigger>
          <TabsTrigger value="nwc-peg">
            <span className="md:hidden">NWC</span>
            <span className="hidden md:inline">NWC Peg</span>
          </TabsTrigger>
          <TabsTrigger value="bank-corroboration">
            <span className="md:hidden">Bank</span>
            <span className="hidden md:inline">Bank Corroboration</span>
          </TabsTrigger>
          <TabsTrigger value="industry-benchmark">
            <span className="md:hidden">Benchmark</span>
            <span className="hidden md:inline">Industry Benchmark</span>
          </TabsTrigger>
          <TabsTrigger value="executive-summary">
            <span className="md:hidden">Exec Summary</span>
            <span className="hidden md:inline">Executive Summary</span>
          </TabsTrigger>
          <TabsTrigger value="buyer-brief">
            <span className="md:hidden">Buyer</span>
            <span className="hidden md:inline">Buyer Brief</span>
          </TabsTrigger>
          <TabsTrigger value="equity-bridge">
            <span className="md:hidden">Equity</span>
            <span className="hidden md:inline">Equity Bridge</span>
          </TabsTrigger>
          <TabsTrigger value="report">Report</TabsTrigger>
        </TabsList>

        {/* ── Overview tab ─────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Financials */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    Financials Summary
                    {/* D2: the normalization store is the authority on this —
                        say which state it is in rather than leaving the CPA to
                        guess whether an approval stuck. */}
                    {hasFinancials && (
                      <Badge variant={normalizationApproved ? 'default' : 'secondary'}>
                        {normalizationApproved ? 'Normalization approved' : 'Draft'}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!hasFinancials ? (
                    <Alert>
                      <AlertDescription>
                        Financials not yet entered — open EBITDA Workspace to begin.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[320px] text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 text-muted-foreground font-medium">
                              Metric
                            </th>
                            {financialYears.map(fy => (
                              <th
                                key={fy.year}
                                className="text-right py-2 text-muted-foreground font-medium"
                              >
                                FY{fy.year}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(
                            [
                              { label: 'Revenue', key: 'revenue' as const },
                              { label: 'Gross Profit', key: 'gross_profit' as const },
                              { label: 'EBITDA', key: 'ebitda' as const },
                            ] as const
                          ).map(({ label, key }) => (
                            <tr key={key} className="border-b last:border-0">
                              <td className="py-2 text-muted-foreground">{label}</td>
                              {financialYears.map(fy => (
                                <td
                                  key={fy.year}
                                  className="py-2 text-right tabular-nums font-medium"
                                >
                                  {fmtUsd(fy[key])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick actions */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(`/normalization?deal_id=${dealId}`)}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open EBITDA Workspace
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(`/deals/${dealId}/tax`)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Tax Document Review
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(`/reports?deal_id=${dealId}`)}
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    Generate / View Report
                  </Button>
                  {/* PORTAL-TEST-1: the portal links used to go live the moment
                      the deal was created, because the tokens are minted then.
                      But uploads are blocked until scope is configured — so a
                      seller who was sent the link early landed on a portal that
                      refused every file, with the CPA none the wiser. The link
                      is only shareable once the seller can actually use it. */}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    disabled={!deal.seller_token || !scopeConfigured}
                    onClick={() => {
                      if (deal.seller_token) {
                        copyToClipboard(
                          `${window.location.origin}/seller?token=${deal.seller_token}`,
                          'Seller link'
                        )
                      }
                    }}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Seller Link
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    disabled={!deal.buyer_token || !scopeConfigured}
                    onClick={() => {
                      if (deal.buyer_token) {
                        copyToClipboard(
                          `${window.location.origin}/buyer?token=${deal.buyer_token}`,
                          'Buyer link'
                        )
                      }
                    }}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Buyer Link
                  </Button>
                  {/* QOE-TRUTH-AND-JOURNEY-1 R14: the old version of this
                      explanation was gated on `!scopeConfigured` only, so it
                      vanished the instant scope was set even if a token was
                      still missing (e.g. DealParty enrichment hiccup) — two
                      dead buttons and no reason. Show a reason for whichever
                      condition is actually blocking the buttons, in every
                      state, not just one. */}
                  {portalLinkReason && (
                    <p className="text-xs text-muted-foreground">
                      {portalLinkReason}
                      {!scopeConfigured && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => setShowScopeWizard(true)}
                            className="font-medium text-amber-700 underline hover:text-amber-800"
                          >
                            Configure scope
                          </button>
                        </>
                      )}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Documents */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <DocumentList docs={docs} />
                </CardContent>
              </Card>

              {/* Optional CPA-side attachment — the seller portal stays the
                  normal route in, but the CPA often already holds the files. */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Source Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <SourceDocumentUploader dealId={dealId!} />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ── Document Requests tab (DOC-INTAKE-1) ─────────────────────────── */}
        <TabsContent value="doc-requests" className="mt-4">
          <DocumentRequestConfig dealId={dealId} />
        </TabsContent>

        {/* ── Findings tab ─────────────────────────────────────────────────── */}
        <TabsContent value="findings" className="mt-4">
          {/* SCOPE-CONFIG-1 Level 2: what this deal was actually sold sits above
              the grid, because "why is check 41 here when the tier does not
              include it" is a question about scope, not about a score. */}
          <div className="mb-4">
            <NegotiatedScopePanel dealId={dealId!} />
          </div>
          {/* FINDINGS-COUNT-1: a catalogue of 74 unscored checks is not 74
              findings. The honest header separates what has run from what is
              merely in scope.
              QOE-CPA-FIXPACK-1 D2: the "enter financials" state used to be
              latched on `!hasFinancials`, which — with normData permanently
              null from the D2 response-shape bug — hid the grid on every deal
              forever, including ones whose normalization was approved and whose
              report had already been generated. It is now a note above the
              grid, not a replacement for it: the grid is how a CPA records
              scores, so it must stay reachable.
              D4: the count in this copy is the deal's own scope, never 74. */}
          {mappedFindings.length === 0 ? (
            <Alert>
              <AlertDescription>
                No diligence checks returned for this deal.
              </AlertDescription>
            </Alert>
          ) : (
            <>
            {!hasFinancials && (
              <Alert className="mb-3">
                <AlertDescription>
                  Financials not yet entered — enter them in EBITDA Normalization
                  to run the {inScopeCount} diligence checks in scope for this
                  engagement.
                </AlertDescription>
              </Alert>
            )}
            <p className="mb-3 text-sm text-muted-foreground">
              {firedCount === 0
                ? `No checks have returned a result yet · ${inScopeCount} procedures in scope`
                : `${firedCount} of ${inScopeCount} checks in scope have returned a result`}
            </p>
            <FindingsTable
                findings={mappedFindings}
                tier={findingsTier}
                onScoreChange={async (findingNumber, score) => {
                  try {
                    await patchFindingScore(dealId!, findingNumber, { score })
                    toast.success('Score saved')
                  } catch (err) {
                    toast.error(formatApiError(err))
                  }
                }}
              />
            </>
          )}
        </TabsContent>

        {/* ── NWC Peg tab ──────────────────────────────────────────────────── */}
        <TabsContent value="nwc-peg" className="mt-4 flex flex-col gap-4">
          {/* FIELDS-1 P1a: the balance sheet is the peg's ONLY input, and until
              this session there was no form anywhere that asked for it. Mount it
              directly above the card so the "not yet provided" note and the five
              fields that answer it are one view, not one navigation apart. Also
              lives on the Normalization workspace, per the brief. */}
          {dealId && (
            <BalanceSheetInputs
              dealId={dealId}
              onSaved={() => setNwcRefreshKey(k => k + 1)}
            />
          )}
          {dealId && <NWCPegCard dealId={dealId} refreshKey={nwcRefreshKey} />}
        </TabsContent>

        {/* ── Bank Corroboration tab ──────────────────────────────────────────── */}
        <TabsContent value="bank-corroboration" className="mt-4 flex flex-col gap-4">
          {/* FIELDS-1 P0: the intake sits directly above its own output, so the
              "deposits not yet provided" note and the form that answers it are
              in the same view. Saving bumps the nonce to re-read the score. */}
          {dealId && (
            <ProofOfCashInputs
              dealId={dealId}
              onSaved={() => setBankRefreshKey(k => k + 1)}
            />
          )}
          {dealId && <BankCorroborationCard dealId={dealId} refreshKey={bankRefreshKey} />}
        </TabsContent>

        {/* ── Industry Benchmark tab ──────────────────────────────────────── */}
        <TabsContent value="industry-benchmark" className="mt-4">
          {dealId && <IndustryBenchmarkCard dealId={dealId} />}
        </TabsContent>

        {/* ── Executive Summary tab ───────────────────────────────────────────── */}
        <TabsContent value="executive-summary" className="mt-4">
          {dealId && <ExecutiveSummaryPanel dealId={dealId} />}
        </TabsContent>

        {/* ── Buyer Brief tab ──────────────────────────────────────────────── */}
        <TabsContent value="buyer-brief" className="mt-4">
          {dealId && <BuyerDealBriefPanel dealId={dealId} />}
        </TabsContent>

        {/* ── Equity Bridge tab ────────────────────────────────────────────── */}
        <TabsContent value="equity-bridge" className="mt-4 flex flex-col gap-4">
          {/* FIELDS-1 P1b: the bridge's inputs — EV, funded debt, deferred
              revenue, customer deposits and the debt schedule — had no form
              anywhere, so build_equity_bridge() was called with three of its
              nine parameters and every deal concluded "equity = EV". Intake sits
              above its own output, same as the other two capture tabs. */}
          {dealId && (
            <EquityBridgeInputs
              dealId={dealId}
              onSaved={() => setEquityRefreshKey(k => k + 1)}
            />
          )}
          {dealId && <EquityBridgeCard dealId={dealId} refreshKey={equityRefreshKey} />}
        </TabsContent>

        {/* ── Report tab ───────────────────────────────────────────────────── */}
        <TabsContent value="report" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {report.url ? (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 text-green-700">
                      <span className="text-lg leading-tight">✓</span>
                      <div>
                        <p className="font-medium">Report generated</p>
                        {report.generated_at && (
                          <p className="text-sm text-muted-foreground">
                            {fmtDate(report.generated_at)}
                          </p>
                        )}
                        {report.filename && (
                          <p className="text-sm text-muted-foreground">{report.filename}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => window.open(report.url!, '_blank')}>
                        <FileDown className="mr-2 h-4 w-4" />
                        Download Report
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/reports?deal_id=${dealId}`)}
                      >
                        Regenerate
                      </Button>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mt-2">
                    Your report has been generated. A PDF will be mailed to you shortly.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Alert>
                    <AlertDescription>
                      No report generated yet. Complete EBITDA normalization first, then generate
                      the QoE report.
                    </AlertDescription>
                  </Alert>
                  <Button onClick={() => navigate(`/reports?deal_id=${dealId}`)}>
                    Go to Report Generator →
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default DealDetail
